import { TableRowProps } from "@chakra-ui/react";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import {
  ColumnDef,
  ColumnFiltersState,
  OnChangeFn,
  Row,
  RowSelectionState,
  SortingState,
  Updater,
  getCoreRowModel,
  isFunction,
  useReactTable,
} from "@tanstack/react-table";
import React from "react";
import { fmap } from "../../utils";
import { downloadCSVContent, mapDataTableToCSV } from "../../utils/data-table-csv-utils";
import { parseFromStorage } from "../../utils/storage";
import { sortingFns } from "../../utils/tanstack-table";
import { DATA_TABLE_DEFAULT_PAGE_SIZE, mapColumnFiltersState } from "./data-table.utils";
import { DataTableMetaButtonsProps } from "./DataTableMetaButtons";
import useColumnVisibility, { ColumnVisibilityParams } from "./useColumnVisibility";

export type EndpointPage<TRow> = {
  items: TRow[];
  totalCount: number;
};

export type EndpointPageRequest<TFilters> = {
  filters: TFilters;
  limit: number;
  offset: number;
  /** The column's `meta.gqlSortKey`, which is the field name the endpoint expects. */
  sortField: string | null;
  sortOrder: "asc" | "desc";
  /**
   * The per-column filter inputs, keyed by each column's `meta.gqlFilterKey`.
   * They are the header row's own filters, separate from the page-level
   * `filters`, and the endpoint receives them as query params like any other.
   */
  columnFilters: Record<string, unknown>;
};

/**
 * A DataTable backed by an endpoint that pages on the server.
 *
 * The GraphQL sibling of this hook exists because the GraphQL layer returns
 * `{ nodes, totalCount }`; `useQueryDataTable` exists for data that arrives whole
 * and is paged in the browser. Neither fits an endpoint that returns one page
 * plus a count: the first speaks GraphQL, and the second derives the total from
 * the number of rows it was handed, so it would report 25.
 *
 * Filters, sorting and pagination are all inputs to the request rather than
 * operations on the rows. The row count comes from the response.
 */
export default function useEndpointDataTable<
  TRow extends Record<string, unknown>,
  TFilters extends Record<string, unknown>,
  TValue
>(params: {
  /** Prefix for the react-query key; the page's own state is appended to it. */
  queryKey: readonly unknown[];
  fetchPage: (request: EndpointPageRequest<TFilters>) => Promise<EndpointPage<TRow>>;
  /**
   * Gathers every row a CSV export should contain, for the filters and ordering
   * currently applied. Given an endpoint that serves one page at a time, this is
   * the caller's decision — walk the pages, ask the server for a file, or refuse
   * past some size — so it does not default to "whatever is on screen".
   */
  fetchAllForExport: (
    request: Omit<EndpointPageRequest<TFilters>, "limit" | "offset">
  ) => Promise<TRow[]>;
  columns: ColumnDef<TRow, any>[];
  columnVisiblity: Omit<ColumnVisibilityParams<TRow, TValue>, "columns">;
  initialSorting?: SortingState;
  /**
   * Shows the header filter row, one input per column that declares a
   * `meta.gqlFilterKey`. Off by default: a table whose filters all live in the
   * page's filter panel would otherwise gain an empty row of inputs.
   */
  enableColumnFilters?: boolean;
  filters: {
    initialState: TFilters;
    storage?: { key: string; version: number };
  };
  trProps?: (row: TRow) => TableRowProps;
  /**
   * Checkbox selection, for tables with a bulk action.
   *
   * Optional so every existing caller is unaffected: leave it out and the table
   * has no selection state at all, exactly as before.
   *
   * Pass `getRowId`. The default row id is the row's index on the current page,
   * so paging or re-sorting would silently re-point a selection at whichever row
   * now sits at that index.
   */
  rowSelection?: {
    state: RowSelectionState;
    onRowSelectionChange: OnChangeFn<RowSelectionState>;
    enableRowSelection?: boolean | ((row: Row<TRow>) => boolean);
    getRowId?: (originalRow: TRow, index: number) => string;
  };
}) {
  const { columnOptions, columnVisibility, visibleColumns, setVisibleColumns } =
    useColumnVisibility({ ...params.columnVisiblity, columns: params.columns });

  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: DATA_TABLE_DEFAULT_PAGE_SIZE,
  });

  const [sorting, setSorting] = React.useState<SortingState>(params.initialSorting ?? []);

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);

  const $columnFilters = React.useMemo(
    () => mapColumnFiltersState(columnFilters, params.columns),
    [columnFilters, params.columns]
  );

  const { filters, setFilters } = useStoredFilters(params.filters);

  const sortColumn = fmap(sorting[0], (entry) =>
    params.columns.find((column) => getColumnId(column) === entry.id)
  );

  const request: EndpointPageRequest<TFilters> = {
    filters,
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    sortField: sortColumn?.meta?.gqlSortKey ?? null,
    sortOrder: sorting[0]?.desc === true ? "desc" : "asc",
    columnFilters: $columnFilters,
  };

  const query = useQuery({
    placeholderData: keepPreviousData,
    queryKey: [...params.queryKey, request],
    queryFn: () => params.fetchPage(request),
  });

  const totalCount = query.data?.totalCount ?? 0;

  const pageCount = React.useMemo(
    () => Math.ceil(totalCount / pagination.pageSize),
    [pagination.pageSize, totalCount]
  );

  const setFilter = React.useCallback(
    <TKey extends keyof TFilters>(key: TKey, updater: Updater<TFilters[TKey]>) => {
      // Any filter change invalidates the current page number: page 7 of the old
      // result set is not page 7 of the new one.
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setFilters((prev) => ({
        ...prev,
        [key]: isFunction(updater) ? updater(prev[key]) : updater,
      }));
    },
    [setFilters]
  );

  const { fetchAllForExport } = params;

  const exportMutation = useMutation({
    mutationFn: async () =>
      await fetchAllForExport({
        filters: request.filters,
        sortField: request.sortField,
        sortOrder: request.sortOrder,
        columnFilters: request.columnFilters,
      }),
    onSuccess: (rows) => {
      const headers = table
        .getHeaderGroups()
        .flatMap((group) => group.headers)
        .filter((header) => !header.id.startsWith("_"));

      downloadCSVContent(mapDataTableToCSV({ rows, headers }));
    },
  });

  const table = useReactTable({
    data: React.useMemo(() => query.data?.items ?? [], [query.data?.items]),
    columns: params.columns,
    pageCount: pageCount,
    state: {
      columnVisibility,
      pagination,
      columnFilters,
      sorting,
      ...(params.rowSelection ? { rowSelection: params.rowSelection.state } : {}),
    },
    // The server did the paging, the ordering and the filtering; the table must
    // not redo any of them.
    enableColumnFilters: params.enableColumnFilters ?? false,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    sortingFns: sortingFns,
    getCoreRowModel: getCoreRowModel(),
    ...(params.rowSelection
      ? {
          enableRowSelection: params.rowSelection.enableRowSelection ?? true,
          onRowSelectionChange: params.rowSelection.onRowSelectionChange,
          getRowId: params.rowSelection.getRowId,
        }
      : {}),
    onSortingChange: React.useCallback((updater: Updater<SortingState>) => {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setSorting(updater);
    }, []),
    onColumnFiltersChange: React.useCallback((updater: Updater<ColumnFiltersState>) => {
      // Same reason as a page filter change: page 7 of the old result set is not
      // page 7 of the new one.
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setColumnFilters(updater);
    }, []),
    onPaginationChange: setPagination,
  });

  const metaButtonProps: DataTableMetaButtonsProps = {
    columnOptions: columnOptions,
    onChangeVisibleColumns: setVisibleColumns,
    visibleColumns: visibleColumns,
    isRefreshing: query.isFetching,
    onClickRefresh: query.refetch,
    isExporting: exportMutation.isPending,
    onClickExport: exportMutation.mutate,
  };

  return {
    table,
    query,
    totalCount,
    filters,
    setFilter,
    metaButtonProps,
    dataTableProps: {
      metaButtonProps,
      isLoading: query.isPending,
      table: table,
      trProps: params.trProps,
    },
  };
}

function getColumnId<TRow, TValue>(column: ColumnDef<TRow, TValue>): string | undefined {
  return column.id ?? ("accessorKey" in column ? String(column.accessorKey) : undefined);
}

/** Filter state, optionally remembered per table in localStorage. */
function useStoredFilters<TFilters extends Record<string, unknown>>(params: {
  initialState: TFilters;
  storage?: { key: string; version: number };
}) {
  const initialLoadRef = React.useRef(true);
  const storageRef = React.useRef(params.storage);

  const storageKey = React.useMemo(
    () => fmap(storageRef.current, (x) => JSON.stringify(["table-filters", x])),
    []
  );

  const initialStateRef = React.useRef(params.initialState);

  const [filters, setFilters] = React.useState<TFilters>(() => {
    if (storageKey === null) {
      return initialStateRef.current;
    }

    return {
      ...initialStateRef.current,
      ...parseFromStorage({ key: storageKey, storage: localStorage }),
    };
  });

  React.useEffect(() => {
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      return;
    }

    if (storageKey === null) {
      return;
    }

    localStorage.setItem(storageKey, JSON.stringify(filters));
  }, [filters, storageKey]);

  return { filters, setFilters };
}
